// Usa a MESMA instância de api.ts de propósito.
//
// Antes havia uma instância própria aqui, com request interceptor mas SEM
// response interceptor: qualquer 401 em /procurement/disputes/* morria seco, sem
// tentar renovar. E como o DisputeView faz polling a cada 3s, era justamente
// esta a maior fonte de requisições convergindo no instante da expiração.
import api from './api';

/** Uma sessão que a regra de tempo (backend #533) propõe encerrar. Os textos vêm prontos do backend: a tela não traduz a perna. */
export interface SessaoPresa {
  id: number;
  encerrar: boolean;
  perna: 'fim-da-sessao-passou' | 'item-terminal' | 'parada-sem-fim' | null;
  regraEmTexto: string | null;
  /** ISO; o fim da sessão quando ele já passou, senão o último toque. */
  paradaDesde: string | null;
  motivo: string;
  /** Preenchida = há sinal de vida: o POST aborta inteiro. */
  recusa: string | null;
  itemId: number | null;
  status: string | null;
  itemStatus: string | null;
  licitacao: string;
}
export interface PreviaDasPresas {
  total: number;
  encerrar: SessaoPresa[];
  manter: SessaoPresa[];
  recusas: SessaoPresa[];
  avisoFixo: string;
  resumo: string;
}

export const disputeService = {
  // Backend #533 (23/10/2026): encerrar por tempo as sessões presas. A prévia não grava; o POST leva só os ids marcados.
  previaDasPresas: async (): Promise<PreviaDasPresas> => (await api.get('/procurement/disputes/encerrar-presas/previa')).data,
  encerrarPresas: async (ids: number[]): Promise<{ encerradas: number; ids: number[]; recusados: Array<{ id: number; motivo: string }>; resumo: string }> =>
    (await api.post('/procurement/disputes/encerrar-presas', { ids })).data,
  listSessions: async () => (await api.get('/procurement/disputes')).data,
  getSession: async (id: number) => (await api.get(`/procurement/disputes/${id}`)).data,
  createSession: async (data: any) => (await api.post('/procurement/disputes', data)).data,
  updateSession: async (id: number, data: any) => (await api.put(`/procurement/disputes/${id}`, data)).data,
  deleteSession: async (id: number) => (await api.delete(`/procurement/disputes/${id}`)).data,
  startDispute: async (id: number, opts?: { bllBatchId?: string }) =>
    (await api.post(`/procurement/disputes/${id}/start`, opts || {})).data,
  stopDispute: async (id: number) => (await api.post(`/procurement/disputes/${id}/stop`)).data,
  previewPortalRules: async (id: number, opts?: { bllBatchId?: string }) => (await api.post(`/procurement/disputes/${id}/preview-portal-rules`, opts || {}, { timeout: 220000 })).data,
  updateStrategy: async (id: number, data: any) => (await api.post(`/procurement/disputes/${id}/strategy`, data)).data,
  getBidLogs: async (id: number) => (await api.get(`/procurement/disputes/${id}/bids`)).data,
  // Fonte ÚNICA de mensagens: lê `procurement_messages` (a MESMA tabela do
  // Monitorar) pelo item da sessão. Substitui o antigo
  // `rankingData.mensagensSessaoPublica`, que dependia só da extensão e existia
  // em 2 de 54 sessões — a aba ficou vazia por 3 meses sem ninguém notar.
  getMessages: async (id: number) => (await api.get(`/procurement/disputes/${id}/messages`)).data,
  refreshRanking: async (id: number) => (await api.post(`/procurement/disputes/${id}/refresh-ranking`)).data,
  /** Puxa itens+valores do portal pra itemLimits (disputa criada manual sem itens). Sincroniza a proposta se preciso. */
  scrapeItems: async (id: number) => (await api.post(`/procurement/disputes/${id}/scrape-items`, {}, { timeout: 120000 })).data,
  /** BLL only — coleta ranking final via HTTP scrape (PartialUnique idStatus=16 → participantRanking). */
  bllCollectResult: async (id: number, opts?: { bllBatchId?: string }) => (await api.post(`/procurement/disputes/${id}/bll-collect-result`, opts || {}, { timeout: 60000 })).data,
  bncCollectResult: async (id: number, opts?: { bllBatchId?: string }) => (await api.post(`/procurement/disputes/${id}/bnc-collect-result`, opts || {}, { timeout: 60000 })).data,
  /** Lance manual via modal de Lance Final Fechado (Pregão Aberto/Fechado fase fechada). */
  manualBid: async (
    id: number,
    body: { itemNumber: number; valorInformado: number; faseItem?: 'LF' | 'LA' | 'AL' },
  ) => (await api.post(`/procurement/disputes/${id}/manual-bid`, body)).data,

  /**
   * Quanto o vencedor cortou na etapa FECHADA das AFs anteriores.
   *
   * O lance fechado é selado — não há como ver o preço dos concorrentes
   * enquanto ele corre. O histórico é a única âncora disponível na hora de
   * decidir um número irreversível.
   */
  afHistorico: async (): Promise<{ n: number; casos: any[] }> =>
    (await api.get('/procurement/disputes/af-historico')).data,
};
